/**
 * AttributeFieldRenderer - Dynamic field renderer for family attributes
 *
 * Renders appropriate input component based on FamilyAttribute.fieldType.
 * Supports: text, number, select, multioption, textarea, checkbox, color, dimensions, images, url
 */

import React, { useCallback } from 'react';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import type { FamilyAttribute, AttributeFieldType } from '../../../../../common/models/product-family.model';
import { useResolveTranslation } from '../../../../../common/hooks/use-resolve-translation.hook';
import { resolveTranslation } from '../../../../../common/utils/translatable-string';

// =============================================================================
// Types
// =============================================================================

/** FamilyAttribute with all translatable fields resolved to plain strings */
type ResolvedAttribute = Omit<FamilyAttribute, 'fieldLabel' | 'placeholder' | 'helpText' | 'options'> & {
  fieldLabel: string;
  placeholder?: string;
  helpText?: string;
  options?: string[];
};

export interface AttributeFieldRendererProps {
  /** The attribute configuration */
  attribute: FamilyAttribute;
  /** Current value */
  value: unknown;
  /** Change handler */
  onChange: (value: unknown) => void;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
}

// =============================================================================
// Sub-Components
// =============================================================================

/** Error or help line under a control. Every field type ends with one, so it lives here once. */
const FieldMessage: React.FC<{ error?: string; helpText?: string; className?: string }> = ({
  error,
  helpText,
  className,
}) => {
  if (!error && !helpText) return null;
  return (
    <p className={cn('mt-1 text-xs', error ? 'text-destructive' : 'text-muted-foreground', className)}>
      {error || helpText}
    </p>
  );
};

interface TextFieldRendererProps {
  attribute: ResolvedAttribute;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

const TextFieldRenderer: React.FC<TextFieldRendererProps> = ({
  attribute,
  value,
  onChange,
  error,
  disabled,
}) => (
  <div>
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={attribute.placeholder || `Enter ${attribute.fieldLabel.toLowerCase()}`}
      aria-invalid={!!error}
      disabled={disabled}
    />
    <FieldMessage error={error} helpText={attribute.helpText} />
  </div>
);

interface NumberFieldRendererProps {
  attribute: ResolvedAttribute;
  value: number | '';
  onChange: (value: number | null) => void;
  error?: string;
  disabled?: boolean;
}

const NumberFieldRenderer: React.FC<NumberFieldRendererProps> = ({
  attribute,
  value,
  onChange,
  error,
  disabled,
}) => (
  <div>
    <div className="relative">
      <Input
        type="number"
        value={value}
        onChange={(e) => {
          const val = e.target.value;
          onChange(val === '' ? null : Number(val));
        }}
        placeholder={attribute.placeholder || `Enter ${attribute.fieldLabel.toLowerCase()}`}
        aria-invalid={!!error}
        disabled={disabled}
        min={attribute.validation?.min}
        max={attribute.validation?.max}
        className={cn(attribute.unit && 'pe-10')}
      />
      {attribute.unit && (
        <span className="pointer-events-none absolute inset-y-0 end-2.5 flex items-center text-xs text-muted-foreground">
          {attribute.unit}
        </span>
      )}
    </div>
    <FieldMessage error={error} helpText={attribute.helpText} />
  </div>
);

interface TextareaFieldRendererProps {
  attribute: ResolvedAttribute;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

const TextareaFieldRenderer: React.FC<TextareaFieldRendererProps> = ({
  attribute,
  value,
  onChange,
  error,
  disabled,
}) => (
  <div>
    <Textarea
      rows={4}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={attribute.placeholder || `Enter ${attribute.fieldLabel.toLowerCase()}`}
      aria-invalid={!!error}
      disabled={disabled}
    />
    <FieldMessage error={error} helpText={attribute.helpText} />
  </div>
);

interface SelectFieldRendererProps {
  attribute: ResolvedAttribute;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

const SelectFieldRenderer: React.FC<SelectFieldRendererProps> = ({
  attribute,
  value,
  onChange,
  error,
  disabled,
}) => (
  <div>
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="w-full" aria-invalid={!!error}>
        <SelectValue placeholder={attribute.placeholder || `Select ${attribute.fieldLabel.toLowerCase()}`} />
      </SelectTrigger>
      <SelectContent>
        {attribute.options?.map((option) => (
          <SelectItem key={option} value={option}>
            {option}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    <FieldMessage error={error} helpText={attribute.helpText} />
  </div>
);

interface MultioptionFieldRendererProps {
  attribute: ResolvedAttribute;
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  disabled?: boolean;
}

const MultioptionFieldRenderer: React.FC<MultioptionFieldRendererProps> = ({
  attribute,
  value,
  onChange,
  error,
  disabled,
}) => {
  const handleToggle = useCallback(
    (option: string) => {
      if (disabled) return;
      const currentValues = value || [];
      if (currentValues.includes(option)) {
        onChange(currentValues.filter((v) => v !== option));
      } else {
        onChange([...currentValues, option]);
      }
    },
    [value, onChange, disabled]
  );

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {attribute.options?.map((option) => {
          const isSelected = (value || []).includes(option);
          return (
            <button
              key={option}
              type="button"
              aria-pressed={isSelected}
              disabled={disabled}
              onClick={() => handleToggle(option)}
              className={cn(
                'h-7 border px-2.5 text-xs font-medium transition-colors',
                'disabled:cursor-default disabled:opacity-50',
                isSelected
                  ? 'border-foreground bg-muted text-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted/60'
              )}
            >
              {option}
            </button>
          );
        })}
      </div>
      <FieldMessage error={error} helpText={attribute.helpText} />
    </div>
  );
};

interface CheckboxFieldRendererProps {
  attribute: ResolvedAttribute;
  value: boolean;
  onChange: (value: boolean) => void;
  error?: string;
  disabled?: boolean;
}

const CheckboxFieldRenderer: React.FC<CheckboxFieldRendererProps> = ({
  attribute,
  value,
  onChange,
  error,
  disabled,
}) => (
  <div>
    <Label className="gap-2 text-xs text-foreground">
      <Checkbox
        checked={!!value}
        onCheckedChange={(checked) => onChange(checked === true)}
        disabled={disabled}
      />
      {attribute.helpText || attribute.fieldLabel}
    </Label>
    {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
  </div>
);

interface ColorFieldRendererProps {
  attribute: ResolvedAttribute;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

const ColorFieldRenderer: React.FC<ColorFieldRendererProps> = ({
  attribute,
  value,
  onChange,
  error,
  disabled,
}) => (
  <div>
    <div className="flex items-center gap-3">
      {/* Product colour data, not a theme token — the hex stays inline */}
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-label={attribute.fieldLabel}
        className={cn(
          'size-10 shrink-0 appearance-none border bg-transparent p-0',
          '[&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0',
          disabled ? 'cursor-default opacity-50' : 'cursor-pointer'
        )}
      />
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#000000"
        aria-invalid={!!error}
        disabled={disabled}
        className="flex-1"
      />
    </div>
    <FieldMessage error={error} helpText={attribute.helpText} />
  </div>
);

interface DimensionsFieldRendererProps {
  attribute: ResolvedAttribute;
  value: { width?: number; height?: number; depth?: number; unit?: string };
  onChange: (value: { width?: number; height?: number; depth?: number; unit?: string }) => void;
  error?: string;
  disabled?: boolean;
}

const DimensionsFieldRenderer: React.FC<DimensionsFieldRendererProps> = ({
  attribute,
  value,
  onChange,
  error,
  disabled,
}) => {
  const dimensions = value || {};
  const unit = dimensions.unit || attribute.unit || 'cm';

  const handleDimensionChange = useCallback(
    (field: 'width' | 'height' | 'depth', val: string) => {
      onChange({
        ...dimensions,
        [field]: val === '' ? undefined : Number(val),
        unit,
      });
    },
    [dimensions, unit, onChange]
  );

  return (
    <div>
      <div className="flex items-center gap-3">
        <Input
          type="number"
          value={dimensions.width ?? ''}
          onChange={(e) => handleDimensionChange('width', e.target.value)}
          placeholder="Width"
          disabled={disabled}
          className="flex-1"
        />
        <span className="text-xs text-muted-foreground">×</span>
        <Input
          type="number"
          value={dimensions.height ?? ''}
          onChange={(e) => handleDimensionChange('height', e.target.value)}
          placeholder="Height"
          disabled={disabled}
          className="flex-1"
        />
        {dimensions.depth !== undefined && (
          <>
            <span className="text-xs text-muted-foreground">×</span>
            <Input
              type="number"
              value={dimensions.depth ?? ''}
              onChange={(e) => handleDimensionChange('depth', e.target.value)}
              placeholder="Depth"
              disabled={disabled}
              className="flex-1"
            />
          </>
        )}
        <span className="min-w-[30px] text-xs text-muted-foreground">{unit}</span>
      </div>
      <FieldMessage error={error} helpText={attribute.helpText} />
    </div>
  );
};

interface UrlFieldRendererProps {
  attribute: ResolvedAttribute;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
}

const UrlFieldRenderer: React.FC<UrlFieldRendererProps> = ({
  attribute,
  value,
  onChange,
  error,
  disabled,
}) => (
  <div>
    <Input
      type="url"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={attribute.placeholder || 'https://'}
      aria-invalid={!!error}
      disabled={disabled}
    />
    <FieldMessage error={error} helpText={attribute.helpText} />
  </div>
);

interface ImagesFieldRendererProps {
  attribute: ResolvedAttribute;
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  disabled?: boolean;
}

const ImagesFieldRenderer: React.FC<ImagesFieldRendererProps> = ({
  attribute,
  value,
  onChange,
  error,
  disabled,
}) => {
  // Note: Full image upload implementation would require file handling
  // This is a placeholder that shows image URLs
  return (
    <div>
      <div className="border border-dashed bg-muted/40 p-6 text-center">
        <p className="text-xs text-muted-foreground">Image upload coming soon</p>
        {(value || []).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {(value || []).map((url, index) => (
              <span
                key={index}
                className="inline-flex h-7 items-center gap-1 rounded-xl border bg-card ps-2 pe-1 text-xs text-foreground"
              >
                <span>{url.substring(0, 30) + (url.length > 30 ? '...' : '')}</span>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label="Remove image"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => onChange((value || []).filter((_, i) => i !== index))}
                  >
                    <X aria-hidden="true" />
                  </Button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
      <FieldMessage error={error} helpText={attribute.helpText} />
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const AttributeFieldRenderer: React.FC<AttributeFieldRendererProps> = ({
  attribute: rawAttribute,
  value,
  onChange,
  error,
  disabled = false,
}) => {
  const { rt, rtArray } = useResolveTranslation();

  // Resolve translatable fields for display, keeping internal structure intact
  const resolvedFieldLabel = rt(rawAttribute.fieldLabel);
  const resolvedPlaceholder = rt(rawAttribute.placeholder) || undefined;
  const resolvedHelpText = rt(rawAttribute.helpText) || undefined;
  const resolvedOptions = rtArray(rawAttribute.options);

  // Map display option -> English value (for storing English in product data)
  const englishOptions = rawAttribute.options?.map((opt) => resolveTranslation(opt, 'en')) ?? [];
  // display string -> english string lookup
  const displayToEnglish: Record<string, string> = {};
  const englishToDisplay: Record<string, string> = {};
  resolvedOptions.forEach((display, i) => {
    const en = englishOptions[i] ?? display;
    displayToEnglish[display] = en;
    englishToDisplay[en] = display;
  });

  // Create a resolved attribute for sub-components (display strings only)
  const attribute = {
    ...rawAttribute,
    fieldLabel: resolvedFieldLabel,
    placeholder: resolvedPlaceholder,
    helpText: resolvedHelpText,
    options: resolvedOptions,
  } as ResolvedAttribute;

  const renderField = () => {
    switch (attribute.fieldType) {
      case 'text':
        return (
          <TextFieldRenderer
            attribute={attribute}
            value={(value as string) || ''}
            onChange={onChange}
            error={error}
            disabled={disabled}
          />
        );

      case 'number':
        return (
          <NumberFieldRenderer
            attribute={attribute}
            value={(value as number) ?? ''}
            onChange={onChange}
            error={error}
            disabled={disabled}
          />
        );

      case 'textarea':
        return (
          <TextareaFieldRenderer
            attribute={attribute}
            value={(value as string) || ''}
            onChange={onChange}
            error={error}
            disabled={disabled}
          />
        );

      case 'select':
        return (
          <SelectFieldRenderer
            attribute={attribute}
            value={englishToDisplay[(value as string)] || (value as string) || ''}
            onChange={(displayVal) => onChange(displayToEnglish[displayVal] || displayVal)}
            error={error}
            disabled={disabled}
          />
        );

      case 'multioption':
        return (
          <MultioptionFieldRenderer
            attribute={attribute}
            value={((value as string[]) || []).map(v => englishToDisplay[v] || v)}
            onChange={(displayVals) => onChange(displayVals.map(d => displayToEnglish[d] || d))}
            error={error}
            disabled={disabled}
          />
        );

      case 'checkbox':
        return (
          <CheckboxFieldRenderer
            attribute={attribute}
            value={!!value}
            onChange={onChange}
            error={error}
            disabled={disabled}
          />
        );

      case 'color':
        return (
          <ColorFieldRenderer
            attribute={attribute}
            value={(value as string) || ''}
            onChange={onChange}
            error={error}
            disabled={disabled}
          />
        );

      case 'dimensions':
        return (
          <DimensionsFieldRenderer
            attribute={attribute}
            value={(value as { width?: number; height?: number; depth?: number; unit?: string }) || {}}
            onChange={onChange}
            error={error}
            disabled={disabled}
          />
        );

      case 'url':
        return (
          <UrlFieldRenderer
            attribute={attribute}
            value={(value as string) || ''}
            onChange={onChange}
            error={error}
            disabled={disabled}
          />
        );

      case 'images':
        return (
          <ImagesFieldRenderer
            attribute={attribute}
            value={(value as string[]) || []}
            onChange={onChange}
            error={error}
            disabled={disabled}
          />
        );

      default:
        // Fallback to text field for unknown types
        return (
          <TextFieldRenderer
            attribute={attribute}
            value={(value as string) || ''}
            onChange={onChange}
            error={error}
            disabled={disabled}
          />
        );
    }
  };

  // Checkbox doesn't need a separate label
  if (attribute.fieldType === 'checkbox') {
    return <div>{renderField()}</div>;
  }

  return (
    <div>
      <Label className="mb-2 block text-xs font-medium text-muted-foreground">
        {attribute.fieldLabel}
        {attribute.required && <span className="text-destructive"> *</span>}
      </Label>
      {renderField()}
    </div>
  );
};

export default AttributeFieldRenderer;
